"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import { Check, ChevronRight, Lock } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export interface SMECategory {
  label: string
  key: string
}

interface SMECategoryTabsProps {
  categories: SMECategory[]
  activeIndex: number
  onSelect: (index: number) => void
  selectedPlans: Record<string, string>
  isAuthenticated?: boolean
  disabled?: boolean
  showHint?: boolean
  onDismissHint?: () => void
}

export function SMECategoryTabs({
  categories,
  activeIndex,
  onSelect,
  selectedPlans,
  isAuthenticated = true,
  disabled = false,
  showHint = false,
  onDismissHint,
}: SMECategoryTabsProps) {
  const steps = [
    ...categories.map((cat, i) => ({ label: cat.label, index: i })),
    { label: "Premium Summary", index: categories.length },
  ]
  const allDone = categories.every((cat) => !!selectedPlans[cat.key])

  return (
    <TooltipProvider delayDuration={0}>
    <div className="flex items-center gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-white px-6 py-8 [scrollbar-color:theme(colors.slate.300)_transparent] [scrollbar-width:thin] dark:border-slate-700 dark:bg-slate-900 dark:[scrollbar-color:theme(colors.slate.600)_transparent]">
      {steps.map((step, i) => {
        const isActive = step.index === activeIndex
        const isDone =
          step.index < categories.length
            ? !!selectedPlans[categories[step.index]?.key ?? ""]
            : false
        const isSummary = step.index === categories.length

        const button = (
          <button
            onClick={() => !disabled && onSelect(step.index)}
            disabled={disabled}
            className={cn(
              "flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold transition-all",
              disabled && "cursor-not-allowed opacity-60",
              isActive
                ? "border-slate-800 bg-slate-800 text-white dark:border-slate-600 dark:bg-slate-700"
                : isDone
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                  : isSummary && allDone
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                    : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
            )}
          >
            <span
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold",
                isActive
                  ? "bg-white/20 text-white"
                  : isDone
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              )}
            >
              {isDone && !isActive ? (
                <Check className="h-3 w-3" />
              ) : (
                step.index + 1
              )}
            </span>
            {step.label}
            {isSummary && !isAuthenticated && (
              <Lock className="h-3 w-3 opacity-60" />
            )}
            {isDone && !isActive && !isSummary && (
              <span className="ml-1 text-[10px] font-normal text-emerald-600 dark:text-emerald-400">
                ✓
              </span>
            )}
          </button>
        )

        return (
          <div key={step.index} className="relative flex shrink-0 items-center gap-2">
            {isSummary && showHint ? (
              <Tooltip open>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="center"
                  sideOffset={8}
                  showArrow={false}
                  className="relative w-64 max-w-none flex-col items-end gap-2 rounded-xl border border-slate-200 bg-white p-3 text-foreground shadow-lg dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="absolute -left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 border-b border-l border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800" />
                  <p className="w-full text-left text-xs text-slate-600 dark:text-slate-300">
                    Select Plans for each category to view and download proposal from Premium Summary
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDismissHint?.()
                    }}
                    className="text-xs font-semibold text-nova-600 hover:text-nova-700 dark:text-nova-400"
                  >
                    Got it
                  </button>
                </TooltipContent>
              </Tooltip>
            ) : (
              button
            )}

            {i < steps.length - 1 && (
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
            )}
          </div>
        )
      })}

    </div>
    </TooltipProvider>
  )
}
